import { useTranslation } from "react-i18next"
import { Controller } from "react-hook-form"

import laparoliereLogo from "assets/laparoliere-logo.png"
import useLanguage from "components/user-settings/use-change-language"
import { useEmailRegistrationForm } from "./use-form"
import { useRegisterMemberEmail } from "./queries"
import styles from "./styles.module.scss"

const EmailRegistration = (): React.ReactElement => {
  const { t } = useTranslation()
  const registerMemberEmail = useRegisterMemberEmail()
  const {
    handleSubmit,
    setError,
    control,
    watch,
    formState: { errors },
  } = useEmailRegistrationForm()

  const onSubmit = handleSubmit((data) => {
    registerMemberEmail.mutate(data, {
      onError(error: any) {
        const responseData = error?.response?.data
        const body =
          responseData?.data &&
          (responseData.data.code || responseData.data.errors)
            ? responseData.data
            : responseData
        if (body?.code === "form/validation") {
          for (const key in body.errors) {
            setError(key as any, {
              message: body.errors[key][0].message,
            })
          }
        }
      },
    })
  })

  const responseData = (registerMemberEmail.error as any)?.response?.data
  const errorBody =
    responseData?.data && (responseData.data.code || responseData.data.errors)
      ? responseData.data
      : responseData

  if (
    registerMemberEmail.isError &&
    errorBody?.code !== "form/validation"
  ) {
    return (
      <Shell>
        <ErrorState />
      </Shell>
    )
  }

  if (registerMemberEmail.isSuccess) {
    return (
      <Shell>
        <Success email={watch("email")} />
      </Shell>
    )
  }

  return (
    <Shell>
      <div className={styles.intro}>
        <p className={styles.lead}>
          {t("external.members.email_msg_1", { lettersCase: "none" })}
        </p>
        <p className={styles.note}>
          {t("external.members.email_msg_2", { lettersCase: "none" })}
        </p>
      </div>

      <form onSubmit={onSubmit} noValidate>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="member-email">
            {t("auth.email")}
          </label>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <input
                id="member-email"
                type="email"
                autoComplete="email"
                placeholder={t("external.members.email_placeholder")}
                className={
                  errors.email
                    ? `${styles.input} ${styles.inputInvalid}`
                    : styles.input
                }
                {...field}
              />
            )}
          />
          {errors.email?.message && (
            <span className={styles.fieldError}>{errors.email.message}</span>
          )}
        </div>

        <button
          type="submit"
          className={styles.submit}
          disabled={registerMemberEmail.isLoading}
        >
          {registerMemberEmail.isLoading
            ? t("global.processing")
            : t("auth.signUp")}
        </button>
      </form>
    </Shell>
  )
}

function Shell({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation()
  const { language, onChange } = useLanguage()
  const nextLanguage = language === "en" ? "fr" : "en"

  return (
    <div className={styles.page}>
      <section className={styles.card}>
        <div className={styles.brandRow}>
          <div className={styles.brand}>
            <img
              className={styles.logo}
              src={laparoliereLogo}
              alt="La Parolière, centre des femmes"
            />
            <p className={styles.orgName}>
              {t("external.members.org_name", { lettersCase: "none" })}
            </p>
          </div>
          <button
            type="button"
            className={styles.lang}
            onClick={() => onChange(nextLanguage)}
          >
            {nextLanguage === "en" ? "English" : "Français"}
          </button>
        </div>
        {children}
      </section>
    </div>
  )
}

function Success({ email = "" }: { email: string }) {
  const { t } = useTranslation()
  return (
    <>
      <div className={styles.successMark} aria-hidden="true">
        <svg width="56" height="56" viewBox="0 0 56 56" focusable="false">
          <circle cx="28" cy="28" r="28" fill="#e8f6ee" />
          <circle cx="28" cy="28" r="20" fill="#1f9d55" />
          <path
            d="M18.5 28.5l6.2 6.2 12.8-13.4"
            fill="none"
            stroke="#fff"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <h2 className={styles.successTitle}>
        {t("external.members.email_saved")}
      </h2>
      <div className={styles.successCopy}>
        <p className={styles.copy}>{t("external.members.email_registration_msg_1")}</p>
        <p className={styles.copy}>{t("external.members.email_registration_msg_2")}</p>
        <p className={styles.copy}>
          {t("external.members.email_registration_msg_3", { email })}
        </p>
        <p className={styles.copy}>{t("external.members.email_registration_msg_4")}</p>
      </div>
      <p className={styles.contact}>
        <strong>{t("global.phone")} :</strong>{" "}
        {t("client_specific.support_phone")}
        <br />
        <strong>{t("auth.email")} :</strong>{" "}
        {t("client_specific.support_email")}
      </p>
    </>
  )
}

function ErrorState() {
  const { t } = useTranslation()
  return (
    <>
      <h2 className={styles.sectionTitle}>{t("errors.error")}</h2>
      <p className={styles.copy}>
        {t("external.members.email_registration_error_msg", {
          lettersCase: "none",
        })}
      </p>
      <button
        type="button"
        className={styles.submit}
        onClick={() => window.location.reload()}
      >
        {t("errors.unexpected_error.try_again")}
      </button>
    </>
  )
}

export default EmailRegistration
